import React, {
  useEffect, useCallback, useContext, useState, useMemo,
} from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  useMediaQuery,
  Typography,
  Grid,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/styles';

import RouteGetter from '../../routes/RouteGetter';
import { clipRoutePath } from '../../utility/helpers';
import { UploadContext } from './tab-media/useUploadFiles';
import { setOpenMediaUpload } from '../../store/modules/articles/actions';
import CloseBrowserTabDetector from '../../services/common/closeBrowserTabDetector';
import { setAllowChangeProjectStatus } from '../../store/modules/projects/actions';

export const useStyles = makeStyles(() => createStyles({
  modalTitle: {
    fontSize: 24,
    color: '#263245',
    fontFamily: 'Montserrat',
  },
  modalText: {
    fontSize: 16,
    fontFamily: 'Roboto Regular',
  },
  continueAction: {
    color: '#018694',
    borderColor: '#018694',
    textTransform: 'unset',
  },
  cancelAction: {
    color: '#F8221A',
    borderColor: '#F8221A',
    textTransform: 'unset',
  },
}), { name: 'ListenChangeRoute' });

const ListenChangeRoute = () => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState('');
  const uploadContext = useContext(UploadContext);
  const { url } = useRouteMatch();
  const { block } = useHistory();

  const theme = useTheme<Theme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();

  const openHandler = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const isProcessingFiles = useMemo(() => {
    const uploadingFiles = uploadContext.files
      .filter((item) => item.meta.status === 'uploading' || item.meta.status === 'ready').length;
    const downloadingFiles = uploadContext.downloadFiles.filter((item) => item.status === 'downloading').length;
    const processingFiles = uploadingFiles + downloadingFiles;

    return processingFiles > 0;
  }, [uploadContext]);

  useEffect(() => {
    const closeBrowserTabDetector = new CloseBrowserTabDetector({});
    const isProcessing = isProcessingFiles;
    if (isProcessing) {
      closeBrowserTabDetector.init();
    }

    return () => {
        closeBrowserTabDetector.destroy();
    };
  }, [dispatch, isProcessingFiles]);

  useEffect(() => {
    dispatch(setAllowChangeProjectStatus(!isProcessingFiles));
    return () => {
      dispatch(setAllowChangeProjectStatus(true));
    };
  }, [dispatch, isProcessingFiles]);

  useEffect(() => {
    const unregister = block((e) => {
      const previousPath = clipRoutePath(url, 3);
      const currentPath = clipRoutePath(e.pathname, 3);
      // TODO check route work
      const adminPath = RouteGetter.get('project').index; // adminIndex
      const isProcessing = isProcessingFiles;

      if (isProcessing && previousPath !== currentPath && currentPath !== adminPath && previousPath !== adminPath) {
        setNextRoute(e.pathname);
        setOpen(true);
        return false;
      }
    });

    return () => unregister();
  }, [setOpen, setNextRoute, isProcessingFiles, block, url]);


  const cancelUploads = useCallback(() => {
    const timer = setTimeout(() => {
      dispatch(push(nextRoute));
      clearTimeout(timer);
    }, 0);
    dispatch(setOpenMediaUpload(false));
    uploadContext.cancelAllFiles();
    setOpen((prev) => !prev);
  }, [uploadContext, nextRoute, dispatch, setOpen]);

  return (
    <Dialog
      fullScreen={fullScreen}
      disableBackdropClick
      open={open}
      style={{ zIndex: 2147483100 }}
    >
      <DialogContent>
        <Grid container alignItems="center" justify="center" spacing={2}>
          <Grid item style={{ maxWidth: 400 }}>
            <Typography gutterBottom variant="h4" align="center" className={classes.modalTitle} style={{ maxWidth: 400 }}>
              Are you sure you want to leave this project?
            </Typography>
          </Grid>
          <Grid item style={{ maxWidth: 400 }}>
            <Typography gutterBottom variant="body1" align="center" className={classes.modalText} style={{ maxWidth: 400 }}>
              Your transfers are not complete. Would you like to cancel all ongoing transfers?
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container alignItems="center" justify="space-between" style={{ maxWidth: 400, margin: 'auto' }} spacing={1}>
          <Grid item style={{ width: 170 }}>
            <Button onClick={openHandler} className={classes.continueAction} variant="outlined">
              Continue Transfers
            </Button>
          </Grid>
          <Grid item style={{ width: 170 }}>
            <Button onClick={cancelUploads} className={classes.cancelAction} variant="outlined">
              Cancel Transfers
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default ListenChangeRoute;
